import { useEffect, useState } from 'react';
import { Box, CircularProgress, Skeleton, Stack, Typography, alpha, useTheme } from '@mui/material';

const DURACAO_FADE_MS = 400;

interface ICartaoFlutuanteCarregandoProps {
  open: boolean;
  message?: string;
  // 'spinner' (padrão): véu borrado + cartão com spinner e logo, igual sempre foi.
  // 'skeleton': cobre o container com blocos cinza no formato aproximado de um
  // formulário — usado em telas onde o spinner flutuante deixava o carregamento
  // parecendo mais lento/pesado do que realmente era.
  variant?: 'spinner' | 'skeleton';
}

// Cobre o container pai (que deve ter position: relative) com um véu leve e borrado,
// mostrando um cartão pequeno e claro centralizado com spinner + logo — alternativa
// mais suave ao overlay escuro em tela cheia, para telas de leitura de dados.
export const CartaoFlutuanteCarregando: React.FC<ICartaoFlutuanteCarregandoProps> = ({
  open,
  message = 'Carregando...',
  variant = 'spinner',
}) => {
  const theme = useTheme();
  const [montado, setMontado] = useState(open);
  const [entrou, setEntrou] = useState(false);

  useEffect(() => {
    if (open) {
      setMontado(true);
      const raf = requestAnimationFrame(() => setEntrou(true));
      return () => cancelAnimationFrame(raf);
    }

    setEntrou(false);
    const timeout = setTimeout(() => setMontado(false), DURACAO_FADE_MS);
    return () => clearTimeout(timeout);
  }, [open]);

  if (!montado) return null;

  if (variant === 'skeleton') {
    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: theme.zIndex.fab,
          borderRadius: 'inherit',
          backgroundColor: theme.palette.background.paper,
          opacity: entrou ? 1 : 0,
          transition: `opacity ${DURACAO_FADE_MS}ms ease`,
          pointerEvents: entrou ? 'auto' : 'none',
          p: 3,
          overflow: 'hidden',
        }}
      >
        <Stack spacing={1.25} sx={{ maxWidth: 900, mx: 'auto' }}>
          <Stack direction="row" spacing={1.5}>
            <Skeleton variant="rounded" height={40} sx={{ flex: 2 }} />
            <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
          </Stack>
          <Skeleton variant="rounded" height={220} sx={{ mt: 1 }} />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Skeleton variant="rounded" width={140} height={32} />
            <Skeleton variant="rounded" width={140} height={32} />
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: theme.zIndex.fab,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'inherit',
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.55)
          : alpha(theme.palette.common.black, 0.12),
        backdropFilter: 'blur(3px)',
        opacity: entrou ? 1 : 0,
        transition: `opacity ${DURACAO_FADE_MS}ms ease`,
        pointerEvents: entrou ? 'auto' : 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          minWidth: 200,
          px: 4,
          py: 3.5,
          borderRadius: 4,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[8],
          transform: entrou ? 'scale(1)' : 'scale(0.92)',
          transition: `transform ${DURACAO_FADE_MS}ms ease`,
        }}
      >
        <Box position="relative" display="inline-flex">
          <CircularProgress size={64} thickness={3} sx={{ color: theme.palette.primary.main }} />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src="/img/siac-logo.png"
              alt=""
              sx={{ width: 28, height: 28 }}
            />
          </Box>
        </Box>

        <Typography
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 600,
            fontSize: '0.9rem',
          }}
        >
          {message}
        </Typography>
      </Box>
    </Box>
  );
};
